import { API, graphqlOperation } from 'aws-amplify'
import { useEffect, useState } from 'react'
import { syncTeamMembersFromTeam } from './useListTeamMembers'

const op = `query GetTeam($pk: ID) {
    getTeam(pk: $pk) {
        groups
        icon
        name
        nrOfMembers
        positions {
            title
            pk
            sk
            createdAt
            managementRights
            d1
            modifiedAt
            modifiedBy
            trainingPrograms {
                name
                pk
                userType
            }
        }
        members {
            pk
            firstName
            lastName
            email
            acceptedOn
            assignedBy
            assignedOn
            d1
            d2
            floor
            modifiedAt
            position {
                title
                sk
            }
            sk
            suite
            tenant
              phone {
                number
                extension
            }
            workPhone {
                number
                extension
            }
        }
        active
        createdAt
        createdBy
        d1
        description
        modifiedAt
        modifiedBy
        pk
        reportAsHere
        sk
        trainingPrograms
        visibility
        contact {
            d1
            d2
            email
            lastName
            firstName
            pk
            role
            sk
            workPhone {
                extension
                name
                number
            }
            phone {
                extension
                name
                number
            }
        }
    }
}
`

const cache = {}

// Mounted useGetTeam instances, so cache mutations (e.g. a member declining
// their assignment while the team page is open) update the page live instead
// of waiting for a remount refetch.
const subscribers = new Map()

const subscribe = (pk, callback) => {
    if (!subscribers.has(pk)) subscribers.set(pk, new Set())
    subscribers.get(pk).add(callback)
    return () => {
        const forPk = subscribers.get(pk)
        if (!forPk) return
        forPk.delete(callback)
        if (forPk.size === 0) subscribers.delete(pk)
    }
}

const notify = (pk) => {
    const forPk = subscribers.get(pk)
    if (!forPk || !cache[pk]) return
    forPk.forEach((callback) => callback(cache[pk]))
}

const storeTeam = (pk, team) => {
    cache[pk] = team
}

const refetchTeam = async (pk) => {
    try {
        const response = await API.graphql(graphqlOperation(op, { pk }))
        const fresh = response.data.getTeam
        if (!fresh) return
        storeTeam(pk, fresh)
        notify(pk)
        syncTeamMembersFromTeam(pk, fresh.members)
    } catch (response) {
        console.error(response?.errors || response)
    }
}

// Team member entries embedded on the team snapshot the occupant's
// name/phones; the backend re-syncs them when the occupant is edited. Refetch
// the teams currently on screen so open pages pick the changes up without a
// refresh.
if (typeof window !== 'undefined') {
    window.addEventListener('occupant-data-updated', () => {
        subscribers.forEach((_callbacks, pk) => refetchTeam(pk))
    })
}

// Patch one member embedded in the cached team (e.g. stamping acceptedOn).
export const patchTeamMemberInTeamCache = (pk, sk, patch) => {
    const team = cache[pk]
    if (!team?.members?.some((m) => m.sk === sk)) return
    cache[pk] = {
        ...team,
        members: team.members.map((m) => (m.sk === sk ? { ...m, ...patch } : m)),
    }
    notify(pk)
}

// Drop one member from the cached team (e.g. after a declined assignment
// deleted the record) and keep the member count in step.
export const removeTeamMemberFromTeamCache = (pk, sk) => {
    const team = cache[pk]
    if (!team?.members?.some((m) => m.sk === sk)) return
    cache[pk] = {
        ...team,
        members: team.members.filter((m) => m.sk !== sk),
        nrOfMembers: Math.max((team.nrOfMembers ?? 1) - 1, 0),
    }
    notify(pk)
}

function useGetTeam(pk) {
    const [error, setErrors] = useState(null)
    const [loading, setLoading] = useState(true)
    const [data, setData] = useState([])

    useEffect(() => {
        let stale = false

        const handleGetTeam = (data) => {
            if (stale) return
            setData(data)
            setLoading(false)
        }

        async function fetch() {
            const cached = cache[pk]
            if (cached) handleGetTeam(cached)
            try {
                const response = await API.graphql(graphqlOperation(op, { pk }))
                const fresh = response.data.getTeam
                storeTeam(pk, fresh)
                handleGetTeam(fresh)
                // Keep the landing pages' rosters (and member counts) in step
                // with what this page just loaded.
                if (fresh?.members) syncTeamMembersFromTeam(pk, fresh.members)
            } catch (response) {
                console.error(response.errors)
                if (!stale) setErrors(response.errors)
            }
        }

        if (!pk) {
            setLoading(false)
            return undefined
        }

        const unsubscribe = subscribe(pk, handleGetTeam)
        fetch()

        return () => {
            stale = true
            unsubscribe()
        }
    }, [pk])

    return { error, loading, data }
}

export default useGetTeam



